import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Phone } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/Motion";
import { useLang } from "../i18n/LanguageContext";
import { SITE_URL, businessSchema, CONTACT } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: `${SITE_URL}/contact`,
  mainEntity: businessSchema
};

export default function Contact() {
  const { lang, t, data } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error(c.errFill);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(c.ok);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast.error(c.fail);
    } finally {
      setSending(false);
    }
  };

  const infoIcons = [MapPin, Clock, Mail];

  return (
    <>
      <Seo title={c.seoTitle} description={c.seoDesc} path="/contact" schema={schema} />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-28 texture-grain" data-testid="contact-page">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-6">{c.kicker}</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.0] tracking-tight max-w-4xl">
          <MaskedLines lines={c.heroLines} />
        </h1>

        <div className="mt-20 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-ink/10 rounded-lg p-8 lg:p-12 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted2 block mb-2">{c.name}</label>
                  <Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={c.namePh} className="h-12" />
                </div>
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted2 block mb-2">{c.email}</label>
                  <Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" className="h-12" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted2 block mb-2">{c.phone}</label>
                  <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(215) 555-0100" className="h-12" />
                </div>
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted2 block mb-2">{c.service}</label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger data-testid="contact-service-select" className="h-12">
                      <SelectValue placeholder={c.selectPh} />
                    </SelectTrigger>
                    <SelectContent>
                      {data.SERVICES.map((s) => (
                        <SelectItem key={s.slug} value={s.title} data-testid={`service-option-${s.slug}`}>{s.title}</SelectItem>
                      ))}
                      <SelectItem value="Other / Not sure">{c.other}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted2 block mb-2">{c.message}</label>
                <Textarea data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} placeholder={c.msgPh} />
              </div>
              <Button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="rounded-full bg-ink text-cream px-10 py-6 font-semibold hover:bg-periwinkle-deep transition-colors duration-300 text-base"
              >
                {sending ? c.sending : c.submit}
              </Button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5 space-y-10">
            <div>
              <h2 className="font-serif text-3xl font-semibold">{c.nextH}</h2>
              <p className="mt-4 text-muted2 leading-relaxed">{c.nextP}</p>
            </div>
            <div className="space-y-6">
              {c.cards.map(({ t: title, d }, i) => {
                const Icon = infoIcons[i];
                return (
                  <div key={title} className="flex gap-4 bg-white border border-ink/10 rounded-lg p-6">
                    <Icon size={20} className="text-periwinkle-deep shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold">{title}</h3>
                      <p className="text-sm text-muted2 mt-1">{d}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="bg-ink texture-grain rounded-lg p-8 space-y-4">
              <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-3 text-cream hover:text-periwinkle-light transition-colors duration-300">
                <Phone size={18} className="text-periwinkle-light" /> {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-cream hover:text-periwinkle-light transition-colors duration-300 break-all">
                <Mail size={18} className="text-periwinkle-light" /> {CONTACT.email}
              </a>
              <p className="flex items-center gap-3 text-cream/70"><MapPin size={18} className="text-periwinkle-light" /> {CONTACT.location}</p>
              <p className="flex items-center gap-3 text-cream/70"><Clock size={18} className="text-periwinkle-light" /> {CONTACT.hours[lang]}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
